/**
 * Real-time service using SignalR for live updates with polling fallback
 */

import * as signalR from '@microsoft/signalr'
import { httpClient } from './http'
import { eventProcessor, type EventPayload } from '../core/events/processor'
import { userContextService } from '../core/infrastructure/user-context-service'
import { logger } from '../core/utils/logger'
import { leaderElection } from './leader-election'
import { eventBroadcaster } from './broadcast'
import { memoryStateService } from '../core/state/memory-store'

export type MessageType = 'scenario' | 'list' | 'item' | 'metadata'
export type MessageAction = 'created' | 'updated' | 'deleted' | 'refreshed'

export interface RealtimeMessage {
  type: MessageType | string  // Can be 'user_{userId}' or 'group_{scenarioId}' from SignalR
  entityType?: MessageType  // Explicit entity type from backend
  entityId?: string
  action: MessageAction
  userId?: string  // User who triggered the change
  sessionId?: string  // Session ID to differentiate browser instances
  data?: any
}

type ChangeHandler = (message?: any) => void

const RECONNECT_DELAY = 5000 // 5 seconds

class RealtimeService {
  private connection: signalR.HubConnection | null = null
  private isActive = false
  private changeHandlers: Set<ChangeHandler> = new Set()
  private subscribedScenarios = new Set<string>()
  private reconnectTimeout: number | null = null
  private usingSignalR = false
  private isConnecting = false
  private notifyTimeout: number | null = null
  private gapCheckTimeout: number | null = null
  private lastGapCheck: number = 0
  private pendingOperations = new Set<string>()  // Track in-flight operations
  private retryingScenarios = new Set<string>()  // Track scenarios with pending retry
  private subscriptionRefCounts = new Map<string, number>()
  private leaderUnsubscribe: (() => void) | null = null
  private pendingGapCheckOnReconnect = false  // Flag for gap check when visibility changes during reconnection

  /**
   * True when SignalR is connected and usable.
   * (Not just "initialized".)
   */
  isConnected(): boolean {
    return (
      this.usingSignalR &&
      this.connection?.state === signalR.HubConnectionState.Connected
    )
  }
  
  // Lifecycle event handlers
  private connectedHandlers = new Set<() => void>()
  private reconnectedHandlers = new Set<() => void>()
  private connectionFailedHandlers = new Set<() => void>()
  private scenarioGroupJoinedHandlers = new Set<(scenarioId: string) => void>()
  private userGroupJoinedHandlers = new Set<() => void>()
  
  // Message handling (still callback-based for now)
  private onMessageReceivedCallback?: (message: EventPayload) => Promise<void>

  /**
   * Listen for initial connection events
   */
  onConnected(handler: () => void): () => void {
    this.connectedHandlers.add(handler)
    return () => this.connectedHandlers.delete(handler)
  }

  /**
   * Listen for reconnection events
   */
  onReconnected(handler: () => void): () => void {
    this.reconnectedHandlers.add(handler)
    return () => this.reconnectedHandlers.delete(handler)
  }

  /**
   * Listen for connection failure events
   */
  onConnectionFailed(handler: () => void): () => void {
    this.connectionFailedHandlers.add(handler)
    return () => this.connectionFailedHandlers.delete(handler)
  }

  /**
   * Wait for SignalR connection to be established.
   * @param timeoutMs - Maximum time to wait in milliseconds
   * @throws Error if timeout is reached
   */
  async waitForConnection(timeoutMs: number): Promise<void> {
    if (this.isConnected()) {
      return
    }

    return await new Promise<void>((resolve, reject) => {
      let unsubConnected: (() => void) | null = null
      let unsubReconnected: (() => void) | null = null

      const cleanup = () => {
        if (unsubConnected) unsubConnected()
        if (unsubReconnected) unsubReconnected()
        clearTimeout(timeout)
      }

      const onNowConnected = () => {
        if (!this.isConnected()) return
        cleanup()
        resolve()
      }

      const timeout = setTimeout(() => {
        cleanup()
        reject(new Error(`SignalR connection timeout after ${timeoutMs}ms`))
      }, timeoutMs)

      unsubConnected = this.onConnected(onNowConnected)
      unsubReconnected = this.onReconnected(onNowConnected)
    })
  }

  /**
   * Listen for scenario group join events
   */
  onScenarioGroupJoined(handler: (scenarioId: string) => void): () => void {
    this.scenarioGroupJoinedHandlers.add(handler)
    return () => this.scenarioGroupJoinedHandlers.delete(handler)
  }

  /**
   * Listen for user group join events
   */
  onUserGroupJoined(handler: () => void): () => void {
    this.userGroupJoinedHandlers.add(handler)
    return () => this.userGroupJoinedHandlers.delete(handler)
  }

  /**
   * Set handler for incoming messages (for queuing/processing logic)
   */
  setMessageReceivedHandler(handler: (message: EventPayload) => Promise<void>) {
    this.onMessageReceivedCallback = handler
  }

  async initialize(): Promise<void> {
    if (this.isActive) {
      logger.debug('Realtime service already active')
      // If we are leader but not connected (e.g. user logged in), try connecting
      if (leaderElection.isLeaderTab() && !this.connection) {
        logger.info('[RealtimeService] Active but not connected, retrying connection')
        this.connectSignalR()
      }
      return
    }

    this.isActive = true
    logger.info('Starting realtime service with SignalR')

    // Listen for leader changes
    this.leaderUnsubscribe = leaderElection.onLeaderChange((isLeader) => {
      if (isLeader) {
        logger.info('[RealtimeService] This tab is now the leader, connecting to SignalR')
        this.connectSignalR()
      } else {
        logger.info('[RealtimeService] This tab is now a follower, disconnecting from SignalR')
        this.disconnectSignalR()
      }
    })

    // Handle visibility changes
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  }

  cleanup(): void {
    this.isActive = false
    this.disconnectSignalR()
    
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    
    if (this.leaderUnsubscribe) {
      this.leaderUnsubscribe()
      this.leaderUnsubscribe = null
    }
  }

  private async disconnectSignalR(): Promise<void> {
    if (this.connection && this.connection.state !== signalR.HubConnectionState.Disconnected) {
      logger.info('[RealtimeService] Disconnecting from SignalR')
      await this.connection.stop()
    }
  }

  private async connectSignalR(): Promise<void> {
    if (this.isConnecting || (this.connection && this.connection.state !== signalR.HubConnectionState.Disconnected)) {
      logger.debug('[RealtimeService] Connection already in progress or connected, skipping connect request')
      return
    }

    this.isConnecting = true
    try {
      // Get SignalR connection info via httpClient
      const connectionInfo = await httpClient.negotiate()
      
      // Create connection
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(connectionInfo.url, {
          accessTokenFactory: () => connectionInfo.accessToken
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: () => RECONNECT_DELAY
        })
        .configureLogging(signalR.LogLevel.Information)
        .build()

      // Set up event handlers
      this.connection.on('messageReceived', (message) => {
        logger.info('[SignalR] 🔔 Message received from server:', message)
        this.handleSignalRMessage(message)
      })

      this.connection.onreconnecting(() => {
        logger.debug('SignalR reconnecting...')
        this.usingSignalR = false
      })

      this.connection.onreconnected(async () => {
        logger.info('SignalR reconnected')
        this.usingSignalR = true
        
        // Emit reconnected event for sync orchestration
        this.emitReconnected()
        
        // Re-subscribe to user's personal group
        await this.subscribeToUserGroup()
        // Re-subscribe to all scenario groups
        await this.resubscribeToScenarios()
        // Check for missed events in all subscribed scenarios
        // Always run after reconnection, or if visibility changed during reconnection
        if (this.pendingGapCheckOnReconnect) {
          this.pendingGapCheckOnReconnect = false
          logger.debug('[SignalR] Running pending gap check after reconnection (visibility changed during reconnect)')
        }
        await this.checkForGapsInAllScenarios()
      })

      this.connection.onclose(() => {
        logger.debug('SignalR connection closed')
        this.usingSignalR = false
        this.scheduleReconnect()
      })

      // Start connection
      await this.connection.start()
      logger.info('SignalR connected successfully')
      this.usingSignalR = true
      
      // Emit connected event for sync orchestration
      this.emitConnected()
      
      // Subscribe to user's personal group for scenarios list updates
      await this.subscribeToUserGroup()
      
      // Subscribe to all currently tracked scenarios
      await this.resubscribeToScenarios()
      
    } catch (error) {
      logger.warn('SignalR connection failed:', error)
      this.usingSignalR = false
      // Emit connection failed event so orchestrators can update health state
      this.emitConnectionFailed()
    } finally {
      this.isConnecting = false
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
    }
    
    this.reconnectTimeout = window.setTimeout(async () => {
      if (this.isActive && !this.usingSignalR) {
        logger.debug('Attempting SignalR reconnect...')
        await this.connectSignalR()
      }
    }, RECONNECT_DELAY)
  }

  /**
   * Handle incoming SignalR event messages
   * All messages from backend are events (type="event" or type="user_{userId}")
   */
  private handleSignalRMessage(message: RealtimeMessage): void {
    logger.debug('[RealtimeService] SignalR message received:', JSON.stringify(message, null, 2))
    
    // Extract event data from message
    // Backend sends: action = eventType, data = Event object (PascalCase from C#)
    const eventType = message.action
    const eventData = message.data || {}
    const scenarioId = eventData.ScenarioId || eventData.scenarioId || message.entityId
    
    if (!eventType || !message.entityId) {
      logger.warn('[RealtimeService] Received malformed event message:', message)
      return
    }
    
    // Handle PascalCase properties from C# (with camelCase fallback)
    const rawPayload = eventData.Payload || eventData.payload || {}
    const eventPayload: EventPayload = {
      eventType: eventType,
      entityType: 'event',
      eventId: eventData.EventId || eventData.eventId || message.entityId,
      scenarioId: scenarioId,
      sequenceNumber: eventData.SequenceNumber ?? eventData.sequenceNumber ?? 0,
      originId: eventData.OriginId || eventData.originId || '',
      commandId: eventData.CommandId || eventData.commandId || '',
      serverTimestamp: eventData.ServerTimestamp || eventData.serverTimestamp || new Date().toISOString(),
      clientTimestamp: eventData.ClientTimestamp || eventData.clientTimestamp || new Date().toISOString(),
      payload: this.normalizePayloadKeys(rawPayload)
    }

    // Delegate to callback (sync-manager handles queuing/processing)
    if (this.onMessageReceivedCallback) {
      logger.info('[SignalR] Using registered callback for event processing')
      this.onMessageReceivedCallback(eventPayload).catch(err => {
        logger.error('[RealtimeService] Error in message callback:', err)
      })
    } else {
      // Fallback: process immediately if no callback registered
      logger.info('[SignalR] Processing event directly (no callback registered)')
      eventProcessor.processEvent(eventPayload)
        .then(async (result) => {
          if (result.status === 'success' && result.requiresMemoryReload) {
            logger.debug(`[SignalR] Event processed, reloading memory and broadcasting: ${result.scenarioId}`)
            await memoryStateService.reloadScenario(result.scenarioId)
            eventBroadcaster.notifyScenarioUpdate(result.scenarioId)
          }
        })
        .catch(err => {
          logger.error('[RealtimeService] Error processing event:', err)
        })
    }
    
    // Notify UI of change
    this.scheduleNotify()
  }

  async stop(): Promise<void> {
    this.isActive = false
    
    if (this.connection) {
      await this.connection.stop()
      this.connection = null
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }

    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    logger.info('Realtime service stopped')
  }

  /**
   * Attempt to reconnect SignalR (half-open circuit breaker probe).
   * Returns true if connection is re-established, false otherwise.
   * Used by ServerSyncService when API is marked down and there are no queued commands.
   */
  async tryReconnect(): Promise<boolean> {
    if (this.isConnected()) {
      return true // Already connected
    }

    if (!this.isActive || !leaderElection.isLeaderTab()) {
      return false
    }

    try {
      await this.connectSignalR()
      return this.isConnected()
    } catch (error) {
      logger.debug('[RealtimeService] tryReconnect failed:', error)
      return false
    }
  }

  private handleVisibilityChange = () => {
    if (document.visibilityState === 'visible' && this.isActive) {
      if (this.usingSignalR) {
        // SignalR connected - check for gaps now
        this.checkForGapsInAllScenarios()
      } else if (this.connection?.state === signalR.HubConnectionState.Reconnecting) {
        // SignalR is reconnecting - flag for gap check when reconnection completes
        this.pendingGapCheckOnReconnect = true
        logger.debug('[SignalR] Visibility changed during reconnection, will check for gaps after reconnect')
      }
    }
  }

  getConnectionStatus(): boolean {
    return this.isActive
  }

  onChange(handler: ChangeHandler): () => void {
    this.changeHandlers.add(handler)
    return () => this.changeHandlers.delete(handler)
  }

  private normalizePayloadKeys(payload: any): any {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      return payload
    }

    const normalized: any = {}
    for (const key in payload) {
      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        // Convert PascalCase to camelCase (e.g., ScenarioName -> scenarioName)
        const camelKey = key.charAt(0).toLowerCase() + key.slice(1)
        normalized[camelKey] = payload[key]
      }
    }
    return normalized
  }

  private scheduleNotify() {
    // Debounce notifications to coalesce rapid updates
    if (this.notifyTimeout) {
      clearTimeout(this.notifyTimeout)
    }
    this.notifyTimeout = window.setTimeout(() => {
      this.notifyTimeout = null
      this.changeHandlers.forEach(handler => handler())
    }, 100)
  }

  // Subscribe to scenario updates
  async subscribeToScenario(scenarioId: string): Promise<void> {
    const currentCount = this.subscriptionRefCounts.get(scenarioId) || 0
    this.subscriptionRefCounts.set(scenarioId, currentCount + 1)

    // Only perform subscription logic if this is the first subscriber
    if (currentCount === 0 && !this.subscribedScenarios.has(scenarioId)) {
      await this.executeSubscription(scenarioId)
    }
  }

  private async executeSubscription(scenarioId: string): Promise<void> {
    // Don't allow multiple retry attempts
    if (this.retryingScenarios.has(scenarioId)) {
      return
    }

    this.subscribedScenarios.add(scenarioId)
    
    if (this.usingSignalR && this.connection) {
      // Add user to scenario group via API
      try {
        const groupName = `group_${scenarioId}`
        await httpClient.addToSignalRGroup(groupName)
        logger.info('[RealtimeService] Subscribed to scenario group:', scenarioId)
        
        // Emit event for sync orchestration
        this.emitScenarioGroupJoined(scenarioId)
        
      } catch (error: any) {
        // 403 Forbidden is expected for newly created scenarios - retry after a delay
        if (error.status === 403) {
          logger.debug('[RealtimeService] Access denied to scenario group (may be newly created), will retry:', scenarioId)
          this.retryingScenarios.add(scenarioId)
          setTimeout(() => {
            // Remove from both sets to allow retry
            this.subscribedScenarios.delete(scenarioId)
            this.retryingScenarios.delete(scenarioId)
            // Retry subscription
            this.executeSubscription(scenarioId).catch(() => {
              logger.warn('[RealtimeService] Retry failed for scenario group:', scenarioId)
            })
          }, 2000) // Retry after 2 seconds
        } else {
          logger.warn('[RealtimeService] Failed to subscribe to scenario group:', error)
        }
      }
    }
  }

  // Unsubscribe from scenario updates
  async unsubscribeFromScenario(scenarioId: string): Promise<void> {
    const currentCount = this.subscriptionRefCounts.get(scenarioId) || 0
    if (currentCount <= 0) return

    const newCount = currentCount - 1
    this.subscriptionRefCounts.set(scenarioId, newCount)

    // Only unsubscribe if no more subscribers
    if (newCount === 0) {
      this.subscribedScenarios.delete(scenarioId)
      
      if (this.usingSignalR && this.connection) {
        try {
          const groupName = `group_${scenarioId}`
          await httpClient.removeFromSignalRGroup(groupName)
        } catch (error) {
          logger.warn('Failed to unsubscribe from scenario group:', error)
        }
      }
    }
  }

  // Subscribe to user's personal group for scenarios list updates
  private async subscribeToUserGroup(): Promise<void> {
    if (!this.usingSignalR || !this.connection) {
      return
    }

    try {
      const userId = userContextService.getCurrentUser()?.userId
      if (!userId) {
        logger.debug('[RealtimeService] Cannot subscribe to user group - no userId')
        return
      }

      const groupName = `user_${userId}`
      await httpClient.addToSignalRGroup(groupName)
      logger.info('[RealtimeService] Subscribed to user group:', groupName)
      
      // Trigger user group sync flow
      await this.onUserGroupConnected()
      
    } catch (error) {
      logger.warn('[RealtimeService] Failed to subscribe to user group:', error)
    }
  }

  /**
   * User group subscription established - emit event
   */
  private onUserGroupConnected(): void {
    logger.debug('[RealtimeService] User group connected - emitting event')
    this.emitUserGroupJoined()
  }
  
  // Called by syncManager to prevent race conditions during sync
  markOperationPending(entityType: string, entityId: string): void {
    const key = `${entityType}:${entityId}`
    this.pendingOperations.add(key)
  }

  clearPendingOperation(entityType: string, entityId: string): void {
    const key = `${entityType}:${entityId}`
    this.pendingOperations.delete(key)
  }

  clearAllPendingOperations(): void {
    this.pendingOperations.clear()
  }
  
  isPending(entityType: string, entityId: string): boolean {
    const key = `${entityType}:${entityId}`
    return this.pendingOperations.has(key)
  }

  private async resubscribeToScenarios(): Promise<void> {
    if (!this.usingSignalR || !this.connection) {
      return
    }

    logger.debug('Re-subscribing to scenario groups:', Array.from(this.subscribedScenarios))
    
    for (const scenarioId of this.subscribedScenarios) {
      try {
        const groupName = `group_${scenarioId}`
        await httpClient.addToSignalRGroup(groupName)
        logger.debug('Re-subscribed to scenario group:', scenarioId)
        
        // Emit event for sync orchestration - the sync service will decide what to sync
        this.emitScenarioGroupJoined(scenarioId)
      } catch (error) {
        console.warn('Failed to re-subscribe to scenario group:', scenarioId, error)
      }
    }
  }

  private async checkForGapsInAllScenarios(): Promise<void> {
    // Debounce: don't check more than once per 5 seconds
    const now = Date.now()
    const MIN_GAP_CHECK_INTERVAL = 5000 // 5 seconds
    
    if (now - this.lastGapCheck < MIN_GAP_CHECK_INTERVAL) {
      logger.debug('[RealtimeService] Gap check skipped (checked recently)')
      return
    }
    
    // Clear any pending timeout and schedule new check
    if (this.gapCheckTimeout) {
      clearTimeout(this.gapCheckTimeout)
    }
    
    this.gapCheckTimeout = window.setTimeout(async () => {
      this.lastGapCheck = Date.now()
      this.gapCheckTimeout = null
      
      logger.debug('[RealtimeService] Checking for gaps in all subscribed scenarios:', Array.from(this.subscribedScenarios))
      
      for (const scenarioId of this.subscribedScenarios) {
        try {
          // Trigger gap recovery check for this scenario
          await eventProcessor.checkForGaps(scenarioId)
        } catch (error) {
          console.warn('[RealtimeService] Failed to check for gaps in scenario:', scenarioId, error)
        }
      }
    }, 100) // Small delay to coalesce multiple rapid calls
  }

  /**
   * Emit lifecycle events for sync orchestration
   */
  private emitConnectionFailed(): void {
    this.connectionFailedHandlers.forEach(handler => {
      try {
        handler()
      } catch (err) {
        logger.error('[RealtimeService] Error in connection failed handler:', err)
      }
    })
  }

  private emitConnected(): void {
    this.connectedHandlers.forEach(handler => {
      try {
        handler()
      } catch (err) {
        logger.error('[RealtimeService] Error in connected handler:', err)
      }
    })
  }

  private emitReconnected(): void {
    this.reconnectedHandlers.forEach(handler => {
      try {
        handler()
      } catch (err) {
        logger.error('[RealtimeService] Error in reconnected handler:', err)
      }
    })
  }

  private emitScenarioGroupJoined(scenarioId: string): void {
    this.scenarioGroupJoinedHandlers.forEach(handler => {
      try {
        handler(scenarioId)
      } catch (err) {
        logger.error('[RealtimeService] Error in scenario group joined handler:', err)
      }
    })
  }

  private emitUserGroupJoined(): void {
    this.userGroupJoinedHandlers.forEach(handler => {
      try {
        handler()
      } catch (err) {
        logger.error('[RealtimeService] Error in user group joined handler:', err)
      }
    })
  }
}

export const realtimeService = new RealtimeService()
